import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import './CourseStageModal.css';
import { useConfirm } from '../contexts/ConfirmContext';

const CourseStageModal = ({ isOpen, onClose, onRefreshStages }) => {
    const [stages, setStages] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [order, setOrder] = useState(0);
    const [editingStageId, setEditingStageId] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchStages();
        }
    }, [isOpen]);

    const fetchStages = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/course-stages`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStages(res.data);
        } catch (err) {
            console.error('Error fetching stages:', err);
            setError('Не вдалося завантажити етапи');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!name.trim()) return;

        try {
            const token = localStorage.getItem('token');
            const data = { name: name.trim(), description, order: Number(order) };
            
            if (editingStageId) {
                await axios.put(`${API_URL}/course-stages/${editingStageId}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${API_URL}/course-stages`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            setName('');
            setDescription('');
            setOrder(0);
            setEditingStageId(null);
            fetchStages();
            if (onRefreshStages) onRefreshStages();
        } catch (err) {
            console.error('Error saving stage:', err);
            setError(err.response?.data?.error || 'Помилка збереження етапу');
        }
    };

    const handleEdit = (stage) => {
        setEditingStageId(stage._id);
        setName(stage.name);
        setDescription(stage.description || '');
        setOrder(stage.order || 0);
    };

    const confirm = useConfirm();

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Видалення етапу',
            message: 'Ви впевнені, що хочете видалити цей етап навчання? Курси цього етапу будуть перенесені до загальних.',
            confirmText: 'Видалити',
            cancelText: 'Скасувати'
        });
        if (!isConfirmed) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/course-stages/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchStages();
            if (onRefreshStages) onRefreshStages();
        } catch (err) {
            console.error('Error deleting stage:', err);
            setError('Не вдалося видалити етап');
        }
    };

    const handleCancelEdit = () => {
        setEditingStageId(null);
        setName('');
        setDescription('');
        setOrder(0);
    };

    if (!isOpen) return null;

    return (
        <div className="csm-overlay" onClick={onClose}>
            <div className="csm-content" onClick={e => e.stopPropagation()}>
                <div className="csm-header">
                    <h3>⚙️ Керування етапами навчання</h3>
                    <button className="csm-close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="csm-body">
                    {error && <div className="csm-error">{error}</div>}

                    <form onSubmit={handleSubmit} className="csm-form">
                        <h4>{editingStageId ? '✏️ Редагувати етап' : '➕ Додати новий етап'}</h4>
                        <div className="csm-form-group">
                            <label>Назва етапу</label>
                            <input 
                                type="text" 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                placeholder="Наприклад: Новий співробітник" 
                                required 
                            />
                        </div>
                        <div className="csm-form-group">
                            <label>Опис етапу</label>
                            <input 
                                type="text" 
                                value={description} 
                                onChange={e => setDescription(e.target.value)} 
                                placeholder="Короткий опис для студента" 
                            />
                        </div>
                        <div className="csm-form-group">
                            <label>Порядок сортування</label>
                            <input 
                                type="number" 
                                value={order} 
                                onChange={e => setOrder(e.target.value)} 
                                placeholder="0" 
                            />
                        </div>
                        <div className="csm-form-actions">
                            <button type="submit" className="csm-btn-save">
                                {editingStageId ? 'Оновити' : 'Створити'}
                            </button>
                            {editingStageId && (
                                <button type="button" className="csm-btn-cancel" onClick={handleCancelEdit}>
                                    Скасувати
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="csm-list-section">
                        <h4>📋 Існуючі етапи</h4>
                        {loading ? (
                            <div className="csm-loading">Завантаження...</div>
                        ) : stages.length === 0 ? (
                            <div className="csm-empty">Етапів ще не створено</div>
                        ) : (
                            <div className="csm-list">
                                {stages.map(stage => (
                                    <div key={stage._id} className="csm-list-item">
                                        <div className="csm-item-info">
                                            <strong>{stage.name}</strong>
                                            {stage.description && <p>{stage.description}</p>}
                                            <span className="csm-item-order">Порядок: {stage.order}</span>
                                        </div>
                                        <div className="csm-item-actions">
                                            <button 
                                                className="csm-btn-item-edit" 
                                                onClick={() => handleEdit(stage)}
                                                title="Редагувати"
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                className="csm-btn-item-delete" 
                                                onClick={() => handleDelete(stage._id)}
                                                title="Видалити"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseStageModal;
